'use client';

import { useEffect, useRef, useState, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { MerchSizes } from 'content/products/merch/constants';
import {
  // AirRingColors,
  CgmPatchesShopifyMap,
  SleepMaskShopifyMap,
  dayBlackGlassesShopifyMap,
  dayTransparentGlassesShopifyMap,
  nightBlackGlassesShopifyMap,
  nightTransparentGlassesShopifyMap,
  // productMap_air_ring,
  SizingKitProductShopifyMap,
} from 'content/products/accessories';
import {
  SilverHaloProdConfigMap,
  GoldHaloProdConfigMap,
  SilverHaloDuoProdConfigMap,
  GoldHaloDuoProdConfigMap,
} from 'content/products/ring/stacking/StackingShopifyMap';
import {
  getBlingDiscountPercentage,
  isBlingVisible,
  isGoldHaloSoldOut,
  RingStackType,
  STACK_UNAVAILABLE_SIZES,
} from 'content/products/ring/stacking/constants';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { Currency } from 'utils/Currency';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { Container, useCartStore, useShallow } from '..';
import { ProductCardRing } from './ProductCardRing';
import {
  buildShopRingCards,
  buildRingProChargingCaseColors,
} from './shopRingCards';
import { ProductCardColorAccessory } from './ProductCardColorAccessory';
import { ringProKeyT } from 'content/products/ring-pro/ringProKeyT';
import {
  ProductCardAccessories,
  ProductCardTravelCharger,
} from './ProductCardAccessories';
import { ProductCardM1 } from './ProductCardM1';
import { PackCard } from './PackCard';
import { ProductCardMerch } from './ProductCardMerch';
import { ProductCardSupplements } from './ProductCardSupplements';
import { ProductCardPowerPlug } from './ProductCardPowerPlug';
import { supplementProducts } from 'content/products/supplements';
import { getShopPowerPlugs } from './shopPowerPlugsData';
import { HomePrice, productMap_home } from 'content/products/uhHome';
import { GiftCartProductShopifyMap } from 'content/products/GiftCartProduct';
import { M1ProductData, NewM1Data } from 'content/products/m1/IN';
import { ProductPurchaseOption } from 'content/products/productMap';
import { ProductType } from 'components/shared/Cart/types';
import { ShopSidebar } from './ShopSidebar';
import { useIsCnOPlus } from 'hooks/pricing/useIsCnOPlus';
import { ProductCardBloodVision } from './ProductCardBloodVision';
import {
  getBloodVisionUpsellCards,
  isBloodVisionUpsellStore,
} from 'content/products/bloodVisionUpsell';
import { getBloodVisionSubscriptionCards } from 'content/products/bloodVisionSubscriptionCards';
import type { PackOffer } from 'content/products/packs/types';
// import { OffersProductCard } from './OfferProductCard';
import styles from './AllProducts.module.css';

const GiftRecipientModal = dynamic(
  () => import('components/gift/GiftRecipientModal'),
  { ssr: false },
);

const ProductCardBloodVisionSubscription = dynamic(() =>
  import('./ProductCardBloodVisionSubscription').then(
    (mod) => mod.ProductCardBloodVisionSubscription,
  ),
);

export const AllProducts = ({
  className,
  merchProducts,
  packs = [],
  currency,
  m1OtpConfig,
  m1SubConfig,
}: {
    className?: string;
    merchProducts?: Record<
      string,
      | Array<
          Record<
            string,
            | string
            | number
            | HTMLImageElement
            | Partial<Record<MerchSizes, Record<string, string>>>
          >
        >
      | boolean
    >;
    /** Server-resolved Curated Packs. Empty = the section does not exist. */
    packs?: PackOffer[];
    currency: Currency;
    m1OtpConfig?: NewM1Data[ProductPurchaseOption.oneTimePurchase];
    m1SubConfig?: NewM1Data[ProductPurchaseOption.membership];
  }) => {
    const searchParams = useSearchParams();
    const CartClient = useCartStore(
      useShallow((s) => ({ store: s.store, currency: s.currency })),
    );
    const {
      region,
      store: regionStore,
      currency: regionCurrency,
    } = useRegionLocale();
    const resolvedStore = (CartClient.store ?? regionStore) as ShopifyStore;
    const showBling = useMemo(
      () => isBlingVisible(region, resolvedStore as ShopifyStore | undefined),
      [region, resolvedStore],
    );
    const bloodVisionCards = useMemo(
      () => getBloodVisionUpsellCards(resolvedStore as ShopifyStore | null),
      [resolvedStore],
    );
    const bvSubscriptionCards = useMemo(
      () =>
        getBloodVisionSubscriptionCards(resolvedStore as ShopifyStore | null),
      [resolvedStore],
    );
    const isBloodVisionActive = useMemo(
      () =>
        (isBloodVisionUpsellStore(resolvedStore as ShopifyStore | null) &&
          bloodVisionCards.length > 0) ||
        bvSubscriptionCards.length > 0,
      [resolvedStore, bloodVisionCards.length, bvSubscriptionCards.length],
    );
    // Supplements section temporarily hidden site-wide.
    // Revert to `CartClient.store === ShopifyStore.US` to relaunch.
    const isSupplementsActive = false as boolean;
    const { isCnOPlus } = useIsCnOPlus();
    const t = useTranslations('ring');
    const tRingPro = useTranslations('ring-pro');
    const tHome = useTranslations('homepage');
    const tCommon = useTranslations('common');
    const ringCards = useMemo(
      () =>
        buildShopRingCards(
          resolvedStore as ShopifyStore,
          (CartClient.currency ?? regionCurrency ?? currency) as Currency,
          ringProKeyT(t, tRingPro),
        ),
      [resolvedStore, CartClient.currency, regionCurrency, currency, t, tRingPro],
    );
    const shopPowerPlugs = useMemo(
      () => getShopPowerPlugs(resolvedStore as ShopifyStore, isCnOPlus),
      [resolvedStore, isCnOPlus],
    );
    const ringProChargingCaseColors = useMemo(
      () => buildRingProChargingCaseColors(resolvedStore as ShopifyStore),
      [resolvedStore],
    );
    const homeShopData = useMemo(() => {
      const result: Record<string, Record<string, string | number>> = {};
      for (const store of Object.values(ShopifyStore)) {
        result[store] = {
          shopifyVariantId: productMap_home.variantId[store] || '',
          price: HomePrice[store] || 0,
        };
      }
      return result as Record<ShopifyStore, Record<string, string | number>>;
    }, []);
    const isRingActive = ringCards.length > 0;
    const m1Plans = m1OtpConfig?.plans
      ? (Object.values(m1OtpConfig.plans).filter(Boolean) as M1ProductData[])
      : [];
    const isM1OtpActive = m1Plans.length > 0;
    const isM1Active = isM1OtpActive;
    const m1SubPlans = m1SubConfig?.plans
      ? (Object.values(m1SubConfig.plans).filter(Boolean) as M1ProductData[])
      : [];
    // const isM1SubActive = m1SubPlans.length > 0;
    // const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState<Set<string>>(
      new Set(),
    );
    const [activeBloodVisionOverlayCardId, setActiveBloodVisionOverlayCardId] =
      useState<string | null>(null);
    const [isGiftRecipientModalVisible, setIsGiftRecipientModalVisible] =
      useState(false);

    // Search functionality hidden temporarily

    const matchesSearch = (_title: string) => true;

    // Single authority for the packs section: PackCard refuses to add a variant
    // belonging to another store, so the heading, the sidebar entry and the cards
    // must all key off that same cart store — not `resolvedStore`, whose region
    // fallback would leave an empty section on screen while the cart store is null.
    const visiblePacks = packs.filter(
      (offer) => offer.store === CartClient.store && matchesSearch(offer.title),
    );

    const ringRef = useRef<HTMLDivElement>(null);
    const m1Ref = useRef<HTMLDivElement>(null);
    const bloodVisionRef = useRef<HTMLDivElement>(null);
    const supplementsRef = useRef<HTMLDivElement>(null);
    const merchandiseRef = useRef<HTMLDivElement>(null);
    const accessoriesRef = useRef<HTMLDivElement>(null);
    const packsRef = useRef<HTMLDivElement>(null);
    const powerPlugsRef = useRef<HTMLDivElement>(null);
    const homeHealthRef = useRef<HTMLDivElement>(null);

    // const ringTagRef = useRef<HTMLButtonElement>(null);
    // const m1TagRef = useRef<HTMLButtonElement>(null);
    // const supplementsTagRef = useRef<HTMLButtonElement>(null);
    // const merchandiseTagRef = useRef<HTMLButtonElement>(null);
    // const accessoriesTagRef = useRef<HTMLButtonElement>(null);
    // const bundlesTagRef = useRef<HTMLButtonElement>(null);

    //change this when new categories are added
    const categoryRefs = [
      ...(isRingActive ? [ringRef] : []),
      powerPlugsRef,
      ...(visiblePacks.length > 0 ? [packsRef] : []),
      ...(isBloodVisionActive ? [bloodVisionRef] : []),
      homeHealthRef,
      ...(isM1Active ? [m1Ref] : []),
      accessoriesRef,
      merchandiseRef,
      ...(isSupplementsActive ? [supplementsRef] : []),
    ];
    // These two arrays are index-aligned with each other AND with the JSX
    // section order, because the hash-deep-link effect indexes categoryRefs by
    // the position of the matching name. Keep all three in the same order.
    const categoryNames = [
      ...(isRingActive ? ['ring'] : []),
      'powerplugs',
      ...(visiblePacks.length > 0 ? ['bundles'] : []),
      ...(isBloodVisionActive ? ['blood-vision'] : []),
      'homehealth',
      ...(isM1Active ? ['m1'] : []),
      'addons',
      'merch',
      ...(isSupplementsActive ? ['supplements'] : []),
    ];

    const categoryDisplayNames: Record<string, string> = {
      ring: t('ring.shop.heading.smartRings'),
      powerplugs: t('ring.shop.heading.softwarePlugIns'),
      homehealth: t('ring.shop.heading.homeHealth'),
      m1: t('ring.shop.heading.glucoseMonitoring'),
      'blood-vision': t('ring.shop.heading.bloodVision'),
      // m1sub: 'M1 Subscription',
      addons: tHome('homepage.section.accessories'),
      merch: t('ring.shop.heading.merch'),
      bundles: t('packs.sectionHeading'),
      supplements: t('ring.shop.heading.supplements'),
    };

    const sidebarCategories = categoryNames.map((key) => ({
      key,
      label: categoryDisplayNames[key] || key,
    }));

    // Map category keys to section wrapper DOM ids for IntersectionObserver
    const categoryKeyToSectionId: Record<string, string> = {
      ring: 'smart-ring',
      powerplugs: 'powerplugs',
      homehealth: 'home-health',
      m1: 'glucose-monitoring',
      'blood-vision': 'blood-vision',
      addons: 'addons',
      merch: 'merch',
      bundles: 'bundles',
      supplements: 'supplements',
    };

    const handleCategoryClick = (key: string) => {
      setSelectedCategories(new Set([key]));
      setTimeout(() => scrollToEl(key), 50);
    };

    // Track active category on scroll
    useEffect(() => {
      const sectionIds = categoryNames.map(
        (key) => categoryKeyToSectionId[key] || key,
      );
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              const sectionId = entry.target.id;
              const catKey = Object.entries(categoryKeyToSectionId).find(
                ([, domId]) => domId === sectionId,
              )?.[0];
              if (catKey) {
                setSelectedCategories(new Set([catKey]));
              }
            }
          }
        },
        { rootMargin: '-200px 0px -60% 0px', threshold: 0 },
      );

      sectionIds.forEach((id) => {
        const el = document.getElementById(id);
        if (el) observer.observe(el);
      });

      return () => observer.disconnect();
    }, [categoryNames.join(',')]);

    function scrollToEl(id: string | null) {
      const element = document?.getElementById(id || ''); // Provide an empty string as a fallback
      if (element) {
        const scrollDiv = element.offsetTop - 184;
        window.scrollTo({ top: scrollDiv, behavior: 'smooth' });
      }
    }

    useEffect(() => {
      const scrollToHash = () => {
        const hash = window.location.hash.substring(1);
        if (!hash) return;
        const index = categoryNames.findIndex((cat) => cat === hash);

        requestAnimationFrame(() => {
          if (index !== -1 && categoryRefs[index].current) {
            categoryRefs[index].current?.scrollIntoView({ behavior: 'smooth' });
          } else {
            scrollToEl(hash);
          }
        });
      };

      scrollToHash();
      window.addEventListener('hashchange', scrollToHash);
      return () => window.removeEventListener('hashchange', scrollToHash);
    }, []);

    // const handleScroll = () => {
    //   if (selectedCategories.size > 0) return;
    //   const categories = categoryRefs.map((ref) => ref.current);
    //   categories.forEach((category, index) => {
    //     if (category) {
    //       const { top, height } = category.getBoundingClientRect();
    //       if (
    //         top <= window.innerHeight / 2 &&
    //         top + height >= window.innerHeight / 2
    //       ) {
    //         setActive(categoryNames[index]);
    //       }
    //     }
    //   });
    // };

    // useEffect(() => {
    //   window.addEventListener('scroll', handleScroll);
    //   return () => {
    //     window.removeEventListener('scroll', handleScroll);
    //   };
    // }, [isSupplementsActive]);

    // CONFIG: ring and m1 removel from page
    // const isM1Active = CartClient.store !== ShopifyStore.ROW;

    // const offersTitle = (() => {
    //   switch (CartClient.store) {
    //     case ShopifyStore.IN:
    //       return 'Festive Offers';
    //     default:
    //       return 'Offers';
    //   }
    // })();

    // --- Birthday Discount Logic ---
    const discount = searchParams?.get('discount') || null;
    const birthdayDiscountPercentage = discount
      ? parseInt(discount.slice(-2)) || 15
      : 0; // Extract last 2 characters as percentage, default to 15%
    const isBirthdayDiscountActive =
      discount &&
      discount.length > 0 &&
      searchParams?.get('affiliateCode') === 'birthday';

    // Helper function to calculate discounted price
    const getDiscountedPrice = (originalPrice: number) => {
      if (isBirthdayDiscountActive) {
        return originalPrice * (1 - birthdayDiscountPercentage / 100);
      }
      return originalPrice;
    };

    // Helper function to format price with discount
    const formatPriceWithDiscount = (originalPrice: number) => {
      if (isBirthdayDiscountActive) {
        const discountedPrice = getDiscountedPrice(originalPrice);
        return {
          originalPrice,
          discountedPrice,
          discountPercentage: birthdayDiscountPercentage,
        };
      }
      return {
        originalPrice,
        discountedPrice: originalPrice,
        discountPercentage: 0,
      };
    };

    // Bling's rate is the campaign rate only — it must match what the buy
    // page and cart upsell charge, so it doesn't blend with the birthday code.
    const blingDiscountRate = getBlingDiscountPercentage(resolvedStore);
    const blingDiscountPercentage =
      blingDiscountRate > 0 ? blingDiscountRate.toString() : undefined;
    const blingOfferLabel =
      blingDiscountRate > 0
        ? t('ring.productSelection.stacking.discountTag', {
            percent: blingDiscountRate,
          })
        : undefined;

    const SizingKitProductCard = (
      <ProductCardAccessories
        productType={ProductType.RING}
        imgSrc={'web_v2/shop/addons/sizing-kit.png'}
        // hoverImgSrc={
        //   'web_v2/all-products/accessories/sizing-kit-open-2.png/w_1920'
        // }
        data={SizingKitProductShopifyMap}
        title={t('ring.shop.products.sizingKit.title')}
        description={t('ring.shop.products.sizingKit.description')}
        tag="new"
      />
    );

    return (
      <section className={[styles.root, className].filter(Boolean).join(' ')}>
        <Container>
          <h1
            style={{
              position: 'absolute',
              width: 1,
              height: 1,
              padding: 0,
              margin: -1,
              overflow: 'hidden',
              clip: 'rect(0 0 0 0)',
              whiteSpace: 'nowrap',
              border: 0,
            }}
          >
            {tCommon('header.store')}
          </h1>
          <div className="shop-layout">
            <ShopSidebar
              className={styles.sidebar}
              searchQuery=""
              onSearchChange={() => {}}
              categories={sidebarCategories}
              activeCategories={selectedCategories}
              onCategoryClick={handleCategoryClick}
            />
            <div className="shop-content">
              {isRingActive
                ? (() => {
                    const filteredRings = ringCards.filter((cfg) =>
                      matchesSearch(cfg.title),
                    );
                    if (filteredRings.length === 0) return null;
                    return (
                      <div className="category" ref={ringRef} id="smart-ring">
                        <h2
                          className="head"
                          id="ring"
                          style={{ marginBottom: '16px' }}
                        >
                          {t('ring.shop.heading.smartRings')}
                        </h2>
                        <div className="products">
                          {filteredRings.map((cfg, idx) => (
                            <ProductCardRing
                              key={cfg.variant}
                              {...cfg}
                              isFirst={idx === 0}
                              originalPrice={
                                isBirthdayDiscountActive
                                  ? cfg.fromPrice.toString()
                                  : undefined
                              }
                              discountPercentage={
                                isBirthdayDiscountActive
                                  ? birthdayDiscountPercentage.toString()
                                  : undefined
                              }
                            />
                          ))}
                          {resolvedStore === ShopifyStore.IN &&
                            matchesSearch('Ultrahuman Ring AIR Gift Card') && (
                              <ProductCardAccessories
                                id="ring-gift-card"
                                productType={ProductType.GENERIC}
                                imgSrc="web_v2/shop/air-1/gifting.png"
                                data={
                                  {
                                    [ShopifyStore.IN]: {
                                      shopifyVariantId:
                                        GiftCartProductShopifyMap[
                                          ShopifyStore.IN
                                        ]?.variantId,
                                      price:
                                        GiftCartProductShopifyMap[
                                          ShopifyStore.IN
                                        ]?.price,
                                    },
                                  } as unknown as Record<
                                    ShopifyStore,
                                    Record<string, string | number>
                                  >
                                }
                                title={t('ring.shop.products.giftCard.title')}
                                description={t(
                                  'ring.shop.products.giftCard.description',
                                )}
                                primaryCtaLabel={tHome(
                                  'homepage.cta.giftRingAir',
                                )}
                                onPrimaryCta={() =>
                                  setIsGiftRecipientModalVisible(true)
                                }
                              />
                            )}
                        </div>
                      </div>
                    );
                  })()
                : null}

              {(() => {
                const filtered = shopPowerPlugs.filter((p) =>
                  matchesSearch(p.title),
                );
                if (filtered.length === 0) return null;
                return (
                  <div className="category" ref={powerPlugsRef} id="powerplugs">
                    <h2
                      className="head"
                      id="powerplugs"
                      style={{ marginBottom: '16px' }}
                    >
                      {t('ring.shop.heading.softwarePlugIns')}
                    </h2>
                    <div className="products">
                      {filtered.map((plug, idx) => (
                        <ProductCardPowerPlug
                          key={idx}
                          {...plug}
                          originalPrice={
                            isBirthdayDiscountActive
                              ? plug.year1.price.toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/*
                Third, directly after Software plug-ins — the position live
                production renders it in. The wrapper id stays "bundles" (not
                "packs") because it is an external contract: /shop#bundles deep
                links and any GTM rule keyed on the section id predate the
                rename to "packs", and matching prod costs nothing.
              */}
              {visiblePacks.length > 0 ? (
                <div className="category" ref={packsRef} id="bundles">
                  <h2 className="head" style={{ marginBottom: '16px' }}>
                    {t('packs.sectionHeading')}
                  </h2>
                  <div className="products">
                    {visiblePacks.map((offer) => (
                      <PackCard key={offer.id} offer={offer} />
                    ))}
                  </div>
                </div>
              ) : null}

              {isBloodVisionActive ? (
                <div
                  className="category"
                  ref={bloodVisionRef}
                  id="blood-vision"
                >
                  <h2
                    className="head"
                    id="blood-vision"
                    style={{ marginBottom: '16px' }}
                  >
                    {t('ring.shop.heading.bloodVision')}
                  </h2>
                  <div className="products">
                    {bvSubscriptionCards.map(
                      (card) =>
                        matchesSearch(card.title) && (
                          <ProductCardBloodVisionSubscription
                            key={card.id}
                            card={card}
                          />
                        ),
                    )}
                    {bloodVisionCards.map((card) => (
                      <ProductCardBloodVision
                        key={card.id}
                        card={card}
                        activeOverlayCardId={activeBloodVisionOverlayCardId}
                        setActiveOverlayCardId={
                          setActiveBloodVisionOverlayCardId
                        }
                      />
                    ))}
                  </div>
                </div>
              ) : null}

              {matchesSearch('Ultrahuman Home') && (
                <div className="category" ref={homeHealthRef} id="home-health">
                  <h2
                    className="head"
                    id="homehealth"
                    style={{ marginBottom: '16px' }}
                  >
                    {t('ring.shop.heading.homeHealth')}
                  </h2>
                  <div className="products">
                    <ProductCardAccessories
                      productType={ProductType.GENERIC}
                      imgSrc="web_v2/shop/home/home-1.png"
                      data={homeShopData}
                      title={tHome('homepage.featured.uhHome.title')}
                      description={t('ring.shop.products.home.description')}
                      originalPrice={
                        isBirthdayDiscountActive
                          ? (
                              HomePrice[CartClient.store as ShopifyStore] ?? 0
                            ).toString()
                          : undefined
                      }
                      discountPercentage={
                        isBirthdayDiscountActive
                          ? birthdayDiscountPercentage.toString()
                          : undefined
                      }
                    />
                  </div>
                </div>
              )}

              {isM1Active &&
              (m1OtpConfig || m1SubConfig) &&
              matchesSearch('Ultrahuman M1')
                ? (() => {
                    if (
                      !(m1OtpConfig?.plans && m1Plans.length) &&
                      !(m1SubConfig?.plans && m1SubPlans.length)
                    )
                      return null;
                    return (
                      <div
                        className="category"
                        ref={m1Ref}
                        id="glucose-monitoring"
                      >
                        <h2
                          className="head"
                          id="m1"
                          style={{ marginBottom: '16px' }}
                        >
                          {t('ring.shop.heading.glucoseMonitoring')}
                        </h2>
                        <div className="products">
                          {m1OtpConfig ? (
                            <ProductCardM1
                              plans={m1OtpConfig.plans}
                              directCheckout={m1OtpConfig.directCheckout}
                              defaultProduct={m1OtpConfig.defaultProduct}
                              imgSrc={
                                region === 'US'
                                  ? 'web_v2/shop/m2/m2-otp.png'
                                  : 'web_v2/shop/m1/otp-1.png'
                              }
                              title={t('ring.shop.products.m1Otp.title', {
                                model: region === 'US' ? 'M2' : 'M1',
                              })}
                              description={t(
                                'ring.shop.products.m1Otp.description',
                              )}
                              price={
                                (m1OtpConfig.plans[m1OtpConfig.defaultProduct]
                                  ?.price as number) ?? m1Plans[0]?.price
                              }
                              outOfStock={m1OtpConfig.outOfStock}
                              originalPrice={
                                isBirthdayDiscountActive
                                  ? (
                                      (m1OtpConfig.plans[
                                        m1OtpConfig.defaultProduct
                                      ]?.price as number) ??
                                      m1Plans[0]?.price ??
                                      0
                                    ).toString()
                                  : undefined
                              }
                              discountPercentage={
                                isBirthdayDiscountActive
                                  ? birthdayDiscountPercentage.toString()
                                  : undefined
                              }
                            />
                          ) : null}
                          {m1SubConfig ? (
                            <ProductCardM1
                              plans={m1SubConfig.plans}
                              directCheckout={m1SubConfig.directCheckout}
                              defaultProduct={m1SubConfig.defaultProduct}
                              imgSrc={
                                region === 'US'
                                  ? 'web_v2/shop/m2/m2-sub.png'
                                  : 'web_v2/shop/m1/subscription-1.png'
                              }
                              title={t('ring.shop.products.m1Sub.title', {
                                model: region === 'US' ? 'M2' : 'M1',
                              })}
                              description={t(
                                'ring.shop.products.m1Sub.description',
                              )}
                              price={
                                (m1SubConfig.plans[m1SubConfig.defaultProduct]
                                  ?.price as number) ?? m1SubPlans[0]?.price
                              }
                              outOfStock={m1SubConfig.outOfStock}
                              originalPrice={
                                isBirthdayDiscountActive
                                  ? (
                                      (m1SubConfig.plans[
                                        m1SubConfig.defaultProduct
                                      ]?.price as number) ??
                                      m1SubPlans[0]?.price ??
                                      0
                                    ).toString()
                                  : undefined
                              }
                              discountPercentage={
                                isBirthdayDiscountActive
                                  ? birthdayDiscountPercentage.toString()
                                  : undefined
                              }
                            />
                          ) : null}
                        </div>
                      </div>
                    );
                  })()
                : null}

              {(() => {
                const accessoryTitles = [
                  ...(ringProChargingCaseColors.length > 0
                    ? ['Ring PRO Charging Case']
                    : []),
                  ...(showBling
                    ? ['Bling Eternity Silver', 'Bling Eternity Gold']
                    : []),
                  ...(resolvedStore === ShopifyStore.IN
                    ? ['Ultrahuman Ring AIR Gift Card']
                    : []),
                  'Voyager Travel Charger',
                  ...(resolvedStore !== ShopifyStore.US
                    ? ['Ultrahuman Ring AIR Sizing Kit']
                    : []),
                  'Ultrahuman Blackout Sleep Mask',
                  'Ultrahuman Skins',
                  'Faraday Series: Blue Light Blocking Glasses (Day)',
                  'Faraday Series: Blue Light Blocking Glasses (Night)',
                ];
                if (!accessoryTitles.some((t) => matchesSearch(t))) return null;
                return (
                  <div className="category" ref={accessoriesRef} id="addons">
                    <div
                      className="head "
                      id="addons"
                      style={{ marginBottom: '16px' }}
                    >
                      {tHome('homepage.section.accessories')}
                    </div>
                    <div className="products">
                      {ringProChargingCaseColors.length > 0 &&
                        matchesSearch('Ring PRO Charging Case') && (
                          <ProductCardColorAccessory
                            productType={ProductType.GENERIC}
                            title={t('ring.shop.products.ringProCase.title')}
                            description={t(
                              'ring.shop.products.ringProCase.description',
                            )}
                            colors={ringProChargingCaseColors}
                            tag="NEW"
                            originalPrice={
                              isBirthdayDiscountActive
                                ? Math.min(
                                    ...ringProChargingCaseColors.map(
                                      (c) => c.price,
                                    ),
                                  ).toString()
                                : undefined
                            }
                            discountPercentage={
                              isBirthdayDiscountActive
                                ? birthdayDiscountPercentage.toString()
                                : undefined
                            }
                          />
                        )}
                      {showBling && (
                        <>
                          {matchesSearch('Bling Eternity Silver') && (
                            <ProductCardTravelCharger
                              imgSrc="web_v2/ring-buy/stacking_shop/silver_duo_shop_1_alt.png"
                              hoverImgSrc="web_v2/ring-buy/stacking_shop/silver_duo_shop_2.png"
                              productType={ProductType.RING}
                              sizing={true}
                              tag="NEW"
                              offerLabel={blingOfferLabel}
                              mainImgFit="cover"
                              title={t('ring.shop.products.blingSilver.title')}
                              description={t(
                                'ring.shop.products.blingSilver.description',
                              )}
                              hideOpenSize
                              sizeSubtitle={t(
                                'ring.shop.products.blingSizeSubtitle',
                              )}
                              variants={[
                                {
                                  label: t('ring.shop.variant.single'),
                                  data: SilverHaloProdConfigMap,
                                },
                                {
                                  label: t('ring.shop.variant.duo'),
                                  data: SilverHaloDuoProdConfigMap,
                                },
                              ]}
                              discountPercentage={blingDiscountPercentage}
                            />
                          )}
                          {matchesSearch('Bling Eternity Gold') && (
                            <ProductCardTravelCharger
                              imgSrc="web_v2/ring-buy/stacking_shop/gold_duo_shop_1_alt.png"
                              hoverImgSrc="web_v2/ring-buy/stacking_shop/gold_duo_shop_2.png"
                              productType={ProductType.RING}
                              sizing={true}
                              tag={
                                isGoldHaloSoldOut(
                                  CartClient.store as ShopifyStore | undefined,
                                )
                                  ? 'sold out'
                                  : 'NEW'
                              }
                              offerLabel={blingOfferLabel}
                              mainImgFit="cover"
                              title={t('ring.shop.products.blingGold.title')}
                              description={t(
                                'ring.shop.products.blingGold.description',
                              )}
                              hideOpenSize
                              sizeSubtitle={t(
                                'ring.shop.products.blingSizeSubtitle',
                              )}
                              unavailableSizes={
                                STACK_UNAVAILABLE_SIZES[RingStackType.GOLD_HALO]
                              }
                              variants={[
                                {
                                  label: t('ring.shop.variant.single'),
                                  data: GoldHaloProdConfigMap,
                                },
                                {
                                  label: t('ring.shop.variant.duo'),
                                  data: GoldHaloDuoProdConfigMap,
                                },
                              ]}
                              discountPercentage={blingDiscountPercentage}
                            />
                          )}
                        </>
                      )}
                      {matchesSearch('Ultrahuman Blackout Sleep Mask') && (
                        <ProductCardAccessories
                          productType={ProductType.M1}
                          imgSrc={'web_v2/shop/addons/sleep-mask-1.png'}
                          hoverImgSrc={'web_v2/shop/addons/sleep-mask-2.png'}
                          data={SleepMaskShopifyMap}
                          title={t('ring.shop.products.sleepMask.title')}
                          description={t(
                            'ring.shop.products.sleepMask.description',
                          )}
                          // tag="sold out"
                          originalPrice={
                            isBirthdayDiscountActive
                              ? (
                                  SleepMaskShopifyMap[
                                    CartClient.store as ShopifyStore
                                  ]?.price ?? 0
                                ).toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}
                      {matchesSearch('Ultrahuman Skins') && (
                        <ProductCardAccessories
                          productType={ProductType.M1}
                          imgSrc={'web_v2/all-products/accessories/Patch.png'}
                          hoverImgSrc={
                            'web_v2/all-products/accessories/PatchHover.png'
                          }
                          data={CgmPatchesShopifyMap}
                          title={t('ring.shop.products.skins.title')}
                          description={t(
                            'ring.shop.products.skins.description',
                          )}
                          tag="popular"
                          mainImgFit="cover"
                          originalPrice={
                            isBirthdayDiscountActive
                              ? (
                                  CgmPatchesShopifyMap[
                                    CartClient.store as ShopifyStore
                                  ]?.price ?? 0
                                ).toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}
                      {matchesSearch('Voyager Travel Charger') && (
                        <ProductCardTravelCharger
                          imgSrc="web_v2/shop/addons/voyager-charger.png"
                          productType={ProductType.RING}
                          sizing={true}
                          hideOpenSize
                          tag="NEW"
                          mainImgFit="cover"
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}
                      {resolvedStore === ShopifyStore.US
                        ? null
                        : matchesSearch('Ultrahuman Ring AIR Sizing Kit')
                        ? SizingKitProductCard
                        : null}

                      {matchesSearch(
                        'Faraday Series: Blue Light Blocking Glasses (Day)',
                      ) && (
                        <ProductCardAccessories
                          productType={ProductType.M1}
                          imgSrc={'web_v2/shop/addons/blbg/day-dark.png'}
                          hoverImgSrc={
                            'web_v2/all-products/accessories/glasses/day-black-hover.png'
                          }
                          data={dayBlackGlassesShopifyMap}
                          title={t('ring.shop.products.faradayDay.title')}
                          description={t(
                            'ring.shop.products.faradayDay.description',
                          )}
                          originalPrice={
                            isBirthdayDiscountActive
                              ? (
                                  dayBlackGlassesShopifyMap[
                                    CartClient.store as ShopifyStore
                                  ]?.price ?? 0
                                ).toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}
                      {matchesSearch(
                        'Faraday Series: Blue Light Blocking Glasses (Day)',
                      ) && (
                        <ProductCardAccessories
                          productType={ProductType.M1}
                          imgSrc={'web_v2/shop/addons/blbg/day-light.png'}
                          data={dayTransparentGlassesShopifyMap}
                          title={t('ring.shop.products.faradayDay.title')}
                          description={t(
                            'ring.shop.products.faradayDay.description',
                          )}
                          originalPrice={
                            isBirthdayDiscountActive
                              ? (
                                  dayTransparentGlassesShopifyMap[
                                    CartClient.store as ShopifyStore
                                  ]?.price ?? 0
                                ).toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}
                      {matchesSearch(
                        'Faraday Series: Blue Light Blocking Glasses (Night)',
                      ) && (
                        <ProductCardAccessories
                          productType={ProductType.M1}
                          imgSrc={'web_v2/shop/addons/blbg/night-black.png'}
                          hoverImgSrc={
                            'web_v2/all-products/accessories/glasses/night-black-hover-1.png'
                          }
                          data={nightBlackGlassesShopifyMap}
                          title={t('ring.shop.products.faradayNight.title')}
                          description={t(
                            'ring.shop.products.faradayNight.description',
                          )}
                          originalPrice={
                            isBirthdayDiscountActive
                              ? (
                                  nightBlackGlassesShopifyMap[
                                    CartClient.store as ShopifyStore
                                  ]?.price ?? 0
                                ).toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}

                      {matchesSearch(
                        'Faraday Series: Blue Light Blocking Glasses (Night)',
                      ) && (
                        <ProductCardAccessories
                          productType={ProductType.M1}
                          imgSrc={'web_v2/shop/addons/blbg/night-clear.png'}
                          hoverImgSrc={
                            'web_v2/all-products/accessories/glasses/night-transparent-hover-1.png'
                          }
                          data={nightTransparentGlassesShopifyMap}
                          title={t('ring.shop.products.faradayNight.title')}
                          description={t(
                            'ring.shop.products.faradayNight.description',
                          )}
                          originalPrice={
                            isBirthdayDiscountActive
                              ? (
                                  nightTransparentGlassesShopifyMap[
                                    CartClient.store as ShopifyStore
                                  ]?.price ?? 0
                                ).toString()
                              : undefined
                          }
                          discountPercentage={
                            isBirthdayDiscountActive
                              ? birthdayDiscountPercentage.toString()
                              : undefined
                          }
                        />
                      )}
                    </div>
                  </div>
                );
              })()}

              {(() => {
                const filteredMerch = (
                  merchProducts?.products as Array<
                    Record<
                      string,
                      string | Record<MerchSizes, Record<string, string>>
                    >
                  >
                ).filter((item) => matchesSearch(item.title as string));
                if (filteredMerch.length === 0) return null;
                return (
                  <div className="category" ref={merchandiseRef} id="merch">
                    <h2
                      className="head"
                      id="merch"
                      style={{ marginBottom: '16px' }}
                    >
                      {t('ring.shop.heading.merch')}
                    </h2>
                    <div className="products">
                      {filteredMerch.map(
                        (
                          item: Record<
                            string,
                            string | Record<MerchSizes, Record<string, string>>
                          >,
                          idx: number,
                        ) => {
                          const originalPrice =
                            typeof item.price === 'number' ? item.price : 0;

                          const priceInfo =
                            formatPriceWithDiscount(originalPrice);

                          return (
                            <ProductCardMerch
                              key={idx}
                              productType={ProductType.MERCH}
                              merchData={{
                                ...item,
                                price: priceInfo.discountedPrice.toString(),
                                originalPrice:
                                  priceInfo.originalPrice.toString(),
                                discountPercentage:
                                  priceInfo.discountPercentage.toString(),
                              }}
                              taxes={merchProducts?.taxInclusive as boolean}
                              currency={currency as Currency}
                              tag={item.tag as 'new' | 'popular'}
                            />
                          );
                        },
                      )}
                    </div>
                  </div>
                );
              })()}


              {isSupplementsActive
                ? (() => {
                    const filteredSupplements = supplementProducts.filter(
                      (product) => matchesSearch(product.title),
                    );
                    if (filteredSupplements.length === 0) return null;
                    return (
                      <div
                        className="category"
                        ref={supplementsRef}
                        id="supplements"
                      >
                        <h2
                          className="head"
                          id="supplements"
                          style={{ marginBottom: '16px' }}
                        >
                          {t('ring.shop.heading.supplements')}
                        </h2>
                        <div className="products">
                          {filteredSupplements.map((product) => (
                            <ProductCardSupplements
                              key={product.id}
                              product={product}
                              originalPrice={
                                isBirthdayDiscountActive
                                  ? product.price.toString()
                                  : undefined
                              }
                              discountPercentage={
                                isBirthdayDiscountActive
                                  ? birthdayDiscountPercentage.toString()
                                  : undefined
                              }
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })()
                : null}
            </div>
          </div>
        </Container>
        <GiftRecipientModal
          visible={isGiftRecipientModalVisible}
          onClose={() => setIsGiftRecipientModalVisible(false)}
        />
      </section>
    );
};
